import {
	Alert,
	AlertIcon,
	Badge,
	Box,
	Button,
	Checkbox,
	Flex,
	FormControl,
	FormLabel,
	Icon,
	Input,
	Select,
	Spinner,
	Table,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
	useDisclosure
} from '@chakra-ui/react';
import { format, subDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { MdFilterList, MdGavel, MdRefresh, MdShoppingCart } from 'react-icons/md';
import Card from '../../components/card/Card';
import { LEILAO_BANKS, LeilaoFilters, LeilaoLead } from '../../models/leilao.model';
import { formatPrice } from '../../utils/format';
import { fetchLeilaoLeads, purchaseLeads } from '../../store/features/leilao/leilao.slice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import ConfirmPurchaseModal from './components/ConfirmPurchaseModal';
import MinhasCompras from './components/MinhasCompras';

const isoDay = (date: Date) => format(date, 'yyyy-MM-dd');

export default function Leilao() {
	const dispatch = useAppDispatch();
	const { leads, meta, loading, purchasing, error } = useAppSelector((state: RootState) => state.leilao);
	const balance = useAppSelector((state: RootState) => state.auth.balance);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const confirmModal = useDisclosure();

	const [dateStart, setDateStart] = useState(isoDay(subDays(new Date(), 7)));
	const [dateEnd, setDateEnd] = useState(isoDay(new Date()));
	const [bank, setBank] = useState('');
	const [margemMin, setMargemMin] = useState('');
	const [margemMax, setMargemMax] = useState('');
	const [cpf, setCpf] = useState('');
	const [page, setPage] = useState(1);
	const [selected, setSelected] = useState<string[]>([]);

	const filters: LeilaoFilters = useMemo(
		() => ({
			page,
			limit: 20,
			dateStart,
			dateEnd,
			...(bank && { banks: bank }),
			...(margemMin && { margemMin: Number(margemMin) }),
			...(margemMax && { margemMax: Number(margemMax) }),
			...(cpf.replace(/\D/g, '') && { cpf: cpf.replace(/\D/g, '') })
		}),
		[page, dateStart, dateEnd, bank, margemMin, margemMax, cpf]
	);

	useEffect(() => {
		dispatch(fetchLeilaoLeads(filters));
		// Seleção não sobrevive à troca de página/filtro: o preço total precisa
		// bater com o que está à vista.
		setSelected([]);
	}, [dispatch, filters]);

	// Só faz sentido selecionar quem ainda não está liberado.
	const selectableIds = useMemo(() => leads.filter(l => !l.owned).map(l => l.id), [leads]);
	const allSelected = selectableIds.length > 0 && selected.length === selectableIds.length;

	const total = useMemo(
		() => leads.filter(l => selected.includes(l.id)).reduce((sum, l) => sum + l.price, 0),
		[leads, selected]
	);
	const insufficient = total > balance;

	function toggle(id: string) {
		setSelected(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
	}

	function toggleAll() {
		setSelected(allSelected ? [] : selectableIds);
	}

	function applyFilters() {
		// Trocar filtro sempre volta para a primeira página: manter a página atual
		// mostraria "nenhum lead" num recorte que tem resultado.
		setPage(1);
	}

	async function confirmPurchase() {
		const result = await dispatch(purchaseLeads(selected));
		confirmModal.onClose();
		if ((result as any).payload?.leads) setSelected([]);
	}

	return (
		<Box pt='10px'>
			<Tabs variant='soft-rounded' colorScheme='blue'>
				<TabList mb='16px'>
					<Tab fontSize='sm'>
						<Icon as={MdGavel} mr='6px' /> Catálogo
					</Tab>
					<Tab fontSize='sm'>
						<Icon as={MdShoppingCart} mr='6px' /> Minhas compras
					</Tab>
				</TabList>

				<TabPanels>
					<TabPanel p='0'>
						<Card mb='20px'>
							<Flex align='center' mb='12px'>
								<Icon as={MdFilterList} color='brand.500' mr='8px' />
								<Text fontWeight='800' fontSize='lg' color={textColor}>
									Filtrar leads
								</Text>
							</Flex>
							<Flex gap='12px' flexWrap='wrap' align='flex-end'>
								<FormControl w='160px'>
									<FormLabel fontSize='xs' mb='4px'>
										De
									</FormLabel>
									<Input type='date' size='sm' value={dateStart} onChange={e => setDateStart(e.target.value)} />
								</FormControl>
								<FormControl w='160px'>
									<FormLabel fontSize='xs' mb='4px'>
										Até
									</FormLabel>
									<Input type='date' size='sm' value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
								</FormControl>
								<FormControl w='170px'>
									<FormLabel fontSize='xs' mb='4px'>
										Banco elegível
									</FormLabel>
									<Select size='sm' value={bank} onChange={e => setBank(e.target.value)}>
										<option value=''>Todos</option>
										{LEILAO_BANKS.map(b => (
											<option key={b} value={b}>
												{b}
											</option>
										))}
									</Select>
								</FormControl>
								<FormControl w='140px'>
									<FormLabel fontSize='xs' mb='4px'>
										Margem mín.
									</FormLabel>
									<Input
										type='number'
										size='sm'
										placeholder='0'
										value={margemMin}
										onChange={e => setMargemMin(e.target.value)}
									/>
								</FormControl>
								<FormControl w='140px'>
									<FormLabel fontSize='xs' mb='4px'>
										Margem máx.
									</FormLabel>
									<Input
										type='number'
										size='sm'
										placeholder='—'
										value={margemMax}
										onChange={e => setMargemMax(e.target.value)}
									/>
								</FormControl>
								<FormControl w='170px'>
									<FormLabel fontSize='xs' mb='4px'>
										CPF
									</FormLabel>
									<Input size='sm' placeholder='Somente números' value={cpf} onChange={e => setCpf(e.target.value)} />
								</FormControl>
								<Button size='sm' variant='brand' leftIcon={<Icon as={MdRefresh} />} onClick={applyFilters}>
									Aplicar
								</Button>
							</Flex>
						</Card>

						<Card>
							<Flex justify='space-between' align='center' mb='12px'>
								<Text fontWeight='800' fontSize='lg' color={textColor}>
									Leads disponíveis
								</Text>
								<Text color={subColor} fontSize='sm'>
									{meta ? `${meta.total}${meta.capped ? '+' : ''} no filtro` : ''}
								</Text>
							</Flex>

							{loading ? (
								<Flex justify='center' py='40px'>
									<Spinner color='brand.500' />
								</Flex>
							) : error ? (
								// Falha do catálogo tem cara de erro, não de estoque vazio: sem isso,
								// leilão sem company configurada parecia "acabaram os leads".
								<Alert status='error' borderRadius='12px' my='12px'>
									<AlertIcon />
									<Box>
										<Text fontWeight='700' fontSize='sm'>
											Não foi possível carregar o catálogo
										</Text>
										<Text fontSize='sm'>{error}</Text>
									</Box>
								</Alert>
							) : leads.length === 0 ? (
								<Text color={subColor} fontSize='sm' py='20px'>
									Nenhum lead encontrado com esses filtros.
								</Text>
							) : (
								<Box overflowX='auto'>
									<Table variant='simple' size='sm'>
										<Thead>
											<Tr>
												<Th w='40px'>
													<Checkbox
														isChecked={allSelected}
														isIndeterminate={selected.length > 0 && !allSelected}
														onChange={toggleAll}
													/>
												</Th>
												<Th>Nome</Th>
												<Th>CPF</Th>
												<Th>Telefone</Th>
												<Th isNumeric>Margem</Th>
												<Th isNumeric>Valor</Th>
												<Th isNumeric>Parcelas</Th>
												<Th>Bancos</Th>
												<Th>Admissão</Th>
												<Th>Entrada</Th>
												<Th isNumeric>Preço</Th>
											</Tr>
										</Thead>
										<Tbody>
											{leads.map(lead => (
												<LeadRow
													key={lead.id}
													lead={lead}
													checked={selected.includes(lead.id)}
													onToggle={() => toggle(lead.id)}
												/>
											))}
										</Tbody>
									</Table>
								</Box>
							)}

							<Flex justify='space-between' align='center' mt='16px' gap='12px' flexWrap='wrap'>
								<Flex gap='8px' align='center'>
									<Button
										size='sm'
										variant='outline'
										isDisabled={!meta?.prev || loading}
										onClick={() => setPage(p => Math.max(1, p - 1))}>
										Anterior
									</Button>
									<Text fontSize='sm' color={subColor}>
										Página {meta?.currentPage ?? 1}
									</Text>
									<Button
										size='sm'
										variant='outline'
										isDisabled={!meta?.next || loading}
										onClick={() => setPage(p => p + 1)}>
										Próxima
									</Button>
								</Flex>

								<Flex gap='12px' align='center'>
									<Text fontSize='sm' color={insufficient ? 'red.500' : subColor}>
										{selected.length} selecionado(s) · {formatPrice(total)}
										{insufficient ? ' — saldo insuficiente' : ''}
									</Text>
									<Button
										variant='brand'
										size='sm'
										leftIcon={<Icon as={MdShoppingCart} />}
										isDisabled={selected.length === 0 || insufficient}
										isLoading={purchasing}
										onClick={confirmModal.onOpen}>
										Comprar
									</Button>
								</Flex>
							</Flex>
						</Card>
					</TabPanel>

					<TabPanel p='0'>
						<MinhasCompras />
					</TabPanel>
				</TabPanels>
			</Tabs>

			<ConfirmPurchaseModal
				isOpen={confirmModal.isOpen}
				onClose={confirmModal.onClose}
				count={selected.length}
				total={total}
				balance={balance}
				loading={purchasing}
				onConfirm={confirmPurchase}
			/>
		</Box>
	);
}

function LeadRow({ lead, checked, onToggle }: { lead: LeilaoLead; checked: boolean; onToggle: () => void }) {
	return (
		<Tr opacity={lead.owned ? 0.85 : 1}>
			<Td>
				{/* Lead já liberado não entra na seleção: não há o que cobrar. */}
				<Checkbox isChecked={checked} isDisabled={lead.owned} onChange={onToggle} />
			</Td>
			<Td>{lead.nome || '—'}</Td>
			<Td>{lead.cpf}</Td>
			<Td>{lead.telefone || '—'}</Td>
			<Td isNumeric>{lead.margemDisponivel != null ? formatPrice(lead.margemDisponivel) : '—'}</Td>
			<Td isNumeric>{lead.valorLiberado != null ? formatPrice(lead.valorLiberado) : '—'}</Td>
			<Td isNumeric>{lead.nroParcelas ?? '—'}</Td>
			<Td>{(lead.bancosElegiveis || []).join(', ') || '—'}</Td>
			<Td>{lead.dataAdmissao || '—'}</Td>
			<Td>{format(new Date(lead.createdAt), 'dd/MM/yyyy')}</Td>
			<Td isNumeric>
				{lead.owned ? (
					<Badge colorScheme='green'>no histórico</Badge>
				) : (
					<Flex justify='flex-end' align='center' gap='6px'>
						{lead.tier === 'TODAY' && (
							<Badge colorScheme='blue' fontSize='10px'>
								hoje
							</Badge>
						)}
						{formatPrice(lead.price)}
					</Flex>
				)}
			</Td>
		</Tr>
	);
}
